import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common components
import {
  Form,
  Button,
  DatePicker,
  TextInput,
  DynamicDropdown,
  StaticDropdown,
  FullScreenLoading,
  DynamicMultiSelectDropdown,
} from "src/Components";
// Types
import {
  IBaseHedgerPairAddOrEdit,
  RefrenceRemainingType,
} from "src/Interfaces/automaticMarketMaking";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Requests
import {
  getManualValuesDropdown,
  addBaseHedgerPairTask,
  editBaseHedgerPairTask,
} from "src/Requests/automaticMarketMaking";
import { getBaseInstruments } from "src/Requests/Order/tradeTableRequests";
import { getDeltaHedgeAccountsDropdown } from "src/Requests/deltaHedgeAccounts";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";

interface Props {
  editValues?: IBaseHedgerPairAddOrEdit;
  onSuccess: () => void;
  onClose: () => void;
}
const AddOrEditModal: React.FC<Props> = (props) => {
  // local store
  const [loading, setLoading] = React.useState(false);
  const [formValues, setFormValues] = React.useState(
    props.editValues || ({} as IBaseHedgerPairAddOrEdit)
  );

  const refrenceRemainingTypeOptions: DropdownItem[] = [
    { id: RefrenceRemainingType.Manual, name: "دستی" },
    { id: RefrenceRemainingType.Auto, name: "اتوماتیک" },
  ];

  const getProperFunction = () => {
    if (props.editValues) return editBaseHedgerPairTask(formValues);
    return addBaseHedgerPairTask(formValues);
  };

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const getManualValuesDropdownData = React.useCallback(() => {
    console.log("====formValues?.hedgeInsId", formValues?.hedgeInsId);
    return getManualValuesDropdown(formValues?.hedgeInsId);
  }, [formValues?.hedgeInsId]);

  const clickHandler = () => {
    setLoading(true);
    getProperFunction()
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          props.onSuccess();
          props.onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <div className="flex flex-col justify-center items-center">
      {loading ? <FullScreenLoading /> : <></>}
      <Form className="!w-full  py-8 px-2 !mx-auto">
        <div className="flex flex-row gap-10">
          <div className="flex flex-col gap-3">
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">پایه</span>
              <DynamicDropdown
                dropdown={{
                  name: "baseInsId",
                  placeholder: "پایه",
                }}
                getData={() => getBaseInstruments()}
                onChange={changeHandler}
                value={formValues?.baseInsId}
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">هجر</span>
              <DynamicDropdown
                dropdown={{
                  name: "hedgeInsId",
                  placeholder: "هجر",
                }}
                getData={() => getBaseInstruments()}
                onChange={changeHandler}
                value={formValues?.hedgeInsId}
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">
                انتخاب سررسید مرجع
              </span>
              <StaticDropdown
                dropdown={{
                  name: "refrenceRemainingType",
                  placeholder: "انتخاب سررسید مرجع",
                }}
                options={refrenceRemainingTypeOptions}
                onChange={changeHandler}
                value={formValues?.refrenceRemainingType}
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">سررسید مرجع</span>
              <DynamicDropdown
                dropdown={{
                  name: "refrecnceRemainingManualValue",
                  placeholder: "سررسید مرجع",
                  disabled: formValues?.refrenceRemainingType !== 1,
                }}
                getData={getManualValuesDropdownData}
                onChange={changeHandler}
                inputId={formValues?.hedgeInsId}
                value={formValues?.refrecnceRemainingManualValue}
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">
                تلاطم بلند مدت هجر
              </span>
              <TextInput
                label="تلاطم بلند مدت هجر"
                name="longtermHedgerVolatility"
                onChange={changeHandler}
                value={formValues?.longtermHedgerVolatility?.toString() || ""}
                kind="negativeDecimalNumbers"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">پایه v-100</span>
              <TextInput
                label="پایه v-100"
                name="v_100_Base_Percent"
                onChange={changeHandler}
                value={formValues?.v_100_Base_Percent?.toString() || ""}
                kind="percent"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">هجر v-100</span>
              <TextInput
                label="هجر v-100"
                name="v_100_Hedger_Percent"
                onChange={changeHandler}
                value={formValues?.v_100_Hedger_Percent?.toString() || ""}
                kind="percent"
              />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">
                ضریب iv-inf به تلاطم
              </span>
              <TextInput
                label="ضریب iv-inf به تلاطم"
                name="iV_inf_turbulence"
                onChange={changeHandler}
                value={formValues?.iV_inf_turbulence?.toString() || ""}
                kind="percent"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">
                ضریب عملکرد پایه به هجر
              </span>
              <TextInput
                label="ضریب عملکرد پایه به هجر"
                name="baseToHedgerPerformance"
                onChange={changeHandler}
                value={formValues?.baseToHedgerPerformance?.toString() || ""}
                kind="percent"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">
                ضریب نقدینگی پایه به هجر
              </span>
              <TextInput
                label="ضریب نقدینگی پایه به هجر"
                name="baseToHedgerRate"
                onChange={changeHandler}
                value={formValues?.baseToHedgerRate?.toString() || ""}
                kind="percent"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">ضریب sentiment</span>
              <TextInput
                label="ضریب sentiment"
                name="sentiment"
                onChange={changeHandler}
                value={formValues?.sentiment?.toString() || ""}
                kind="percent"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">سقف iv-30 پایه</span>
              <TextInput
                label="سقف iv-30 پایه"
                name="maxIV_30_Base"
                onChange={changeHandler}
                value={formValues?.maxIV_30_Base?.toString() || ""}
                kind="percent"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">کف iv-30 پایه</span>
              <TextInput
                label="کف iv-30 پایه"
                name="minIV_30_Base"
                onChange={changeHandler}
                value={formValues?.minIV_30_Base?.toString() || ""}
                kind="percent"
              />
            </div>
            <div className="flex w-full items-center justify-start">
              <span className="text-sm text-gray-500 w-72">
                اکانت‌های بازارگردانی
              </span>
              <DynamicMultiSelectDropdown
                dropdown={{
                  placeholder: "اکانت‌های بازارگردانی",
                  name: "accountIds",
                }}
                value={formValues.accountIds}
                onChange={changeHandler}
                getData={getDeltaHedgeAccountsDropdown}
              />
            </div>
          </div>
        </div>
      </Form>
      <div className="mt-6 w-64 self-center">
        <Button
          text={props.editValues ? "ویرایش" : "ثبت"}
          onClick={clickHandler}
          variant="contained"
        />
      </div>
    </div>
  );
};

export default AddOrEditModal;
